import React from 'react';
import { 
    X, ExternalLink, Globe, BookOpen, Layers, Database, ShieldCheck, 
    ArrowRight, CheckCircle2, Zap
} from 'lucide-react';
import Button from '../../../components/Button';

export const IntegrationDetailsModal = ({ integration, onClose, isDarkMode }) => {
    if (!integration) return null;

    const themeCard = isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-gray-200';
    const themeLabel = `block text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`;
    const dynamicMap = Array.isArray(integration.dynamicMap) ? integration.dynamicMap : [];

    return (
        <div 
            className="absolute inset-0 z-[100] !mt-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
            onClick={onClose}
        >
            <div 
                className={`rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border ${
                    isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800 border-gray-200'
                }`}
                onClick={e => e.stopPropagation()}
            >
                {/* Cabeçalho */}
                <div className={`p-5 flex justify-between items-center border-b shrink-0 ${
                    isDarkMode ? 'bg-slate-900/80 border-slate-700' : 'bg-slate-100/80 border-gray-200'
                }`}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-md">
                            <Globe size={20} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="font-bold text-lg">{integration.name || 'Detalhes da Integração'}</h3>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                                    integration.isActive !== false 
                                        ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' 
                                        : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
                                }`}>
                                    {integration.isActive !== false ? 'Ativo' : 'Inativo'}
                                </span>
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                                    {integration.type || 'API REST'}
                                </span>
                            </div>
                            <p className="text-xs opacity-60 font-mono mt-0.5">ID: {integration.id}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {integration.docsUrl && (
                            <a
                                href={integration.docsUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm transition-colors"
                            >
                                <BookOpen size={14} />
                                <span>Documentação Oficial</span>
                                <ExternalLink size={12} className="opacity-75" />
                            </a>
                        )}
                        <button 
                            onClick={onClose}
                            className="p-1.5 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Conteúdo com Scroll */}
                <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-6">
                    
                    {/* Como Funciona / Explicação Funcional */}
                    <div className={`p-4 rounded-xl border ${themeCard}`}>
                        <div className="flex items-center gap-2 mb-2 text-indigo-500 font-bold text-sm">
                            <BookOpen size={16} />
                            <h4>Como Funciona esta Integração</h4>
                        </div>
                        {integration.howItWorks ? (
                            <div className="text-xs leading-relaxed opacity-90 whitespace-pre-line font-sans">
                                {integration.howItWorks}
                            </div>
                        ) : (
                            <p className="text-xs italic opacity-60">
                                Nenhuma explicação detalhada foi fornecida para esta conexão. Você pode adicionar orientações de negócio e regras operacionais editando o cadastro da integração.
                            </p>
                        )}
                    </div>

                    {/* Parâmetros Técnicos & Conexão */}
                    <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider mb-3 opacity-70 flex items-center gap-1.5">
                            <Layers size={14} />
                            Parâmetros Técnicos de Comunicação
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className={`p-3.5 rounded-xl border ${themeCard}`}>
                                <span className={themeLabel}>Endpoint / URL Base</span>
                                <p className="font-mono text-xs break-all select-all font-semibold text-blue-500">
                                    {integration.baseUrl || 'Não configurada'}
                                </p>
                            </div>

                            <div className={`p-3.5 rounded-xl border ${themeCard}`}>
                                <span className={themeLabel}>Método HTTP & Modelo</span>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="px-2 py-0.5 rounded text-xs font-mono font-bold bg-slate-200 dark:bg-slate-700">
                                        {integration.fetchMethod || 'GET'}
                                    </span>
                                    <span className="text-xs opacity-75">
                                        Modelo: <b>{integration.model || 'Padrão'}</b>
                                    </span>
                                </div>
                            </div>

                            <div className={`p-3.5 rounded-xl border ${themeCard}`}>
                                <span className={themeLabel}>Tabela de Destino no Hub (Firestore)</span>
                                <div className="flex items-center gap-2 mt-1">
                                    <Database size={14} className="text-emerald-500" />
                                    <span className="font-mono text-xs font-bold">
                                        {integration.targetTable || 'Não vinculada'}
                                    </span>
                                </div>
                            </div>

                            <div className={`p-3.5 rounded-xl border ${themeCard}`}>
                                <span className={themeLabel}>Caminho do Array na Resposta (Data Path)</span>
                                <p className="font-mono text-xs mt-1">
                                    {integration.dataPath || 'Raiz do JSON (data)'}
                                </p>
                            </div>

                            <div className={`p-3.5 rounded-xl border md:col-span-2 ${themeCard}`}>
                                <span className={themeLabel}>Comportamento de Atualização & Autenticação</span>
                                <div className="flex flex-wrap items-center gap-4 text-xs mt-1">
                                    <div>
                                        Regra: <b>{integration.syncBehavior === 'insert_only' ? 'Ignorar existentes (Apenas novos)' : 'Sobrescrever/Atualizar (Upsert)'}</b>
                                    </div>
                                    <div>
                                        Header de Autenticação: <b className="font-mono">{integration.headerKey || 'Authorization'}</b>
                                    </div>
                                    {integration.headerValue && (
                                        <div className="flex items-center gap-1 text-emerald-500">
                                            <ShieldCheck size={14} />
                                            <span>Token de acesso parametrizado</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Mapeamento Dinâmico de Campos */}
                    <div>
                        <div className="flex justify-between items-center mb-3">
                            <h4 className="text-xs font-bold uppercase tracking-wider opacity-70 flex items-center gap-1.5">
                                <Zap size={14} />
                                Mapeamento De / Para (Campos da API ➔ Firestore)
                            </h4>
                            <span className="text-[11px] opacity-60">
                                {dynamicMap.length} {dynamicMap.length === 1 ? 'campo mapeado' : 'campos mapeados'}
                            </span>
                        </div>

                        {dynamicMap.length > 0 ? (
                            <div className="border rounded-xl overflow-hidden">
                                <table className="w-full text-xs text-left">
                                    <thead className={`uppercase font-bold ${
                                        isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'
                                    }`}>
                                        <tr>
                                            <th className="p-3">Campo na API Externa</th>
                                            <th className="p-3 text-center w-12"></th>
                                            <th className="p-3">Campo no Banco apEx (Firestore)</th>
                                            <th className="p-3">Observação / Finalidade</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y font-mono ${
                                        isDarkMode ? 'divide-slate-700 bg-slate-900/40' : 'divide-gray-100 bg-white'
                                    }`}>
                                        {dynamicMap.map((mapItem, idx) => (
                                            <tr key={idx} className={isDarkMode ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'}>
                                                <td className="p-3 text-indigo-400 font-bold">
                                                    {mapItem.apiField || '-'}
                                                </td>
                                                <td className="p-3 text-center text-slate-400">
                                                    <ArrowRight size={14} className="inline" />
                                                </td>
                                                <td className="p-3 text-emerald-500 font-bold">
                                                    {mapItem.fbField || '-'}
                                                </td>
                                                <td className="p-3 font-sans text-[11px] opacity-75">
                                                    {mapItem.fbField === 'id' ? 'Chave primária do documento' :
                                                     mapItem.fbField === 'salarioAtual' ? 'Salário base atualizado' :
                                                     mapItem.fbField === 'cpf' ? 'CPF com validação' :
                                                     mapItem.fbField === 'matricula' ? 'Matrícula funcional' :
                                                     mapItem.fbField === 'unidadeNome' ? 'Unidade Acad correspondente' :
                                                     'Sincronizado automaticamente'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className={`p-4 rounded-xl border text-center text-xs opacity-60 ${themeCard}`}>
                                Nenhum mapeamento dinâmico de campos registrado para esta conexão.
                            </div>
                        )}
                    </div>
                </div>

                {/* Rodapé */}
                <div className={`p-4 border-t flex justify-between items-center shrink-0 ${
                    isDarkMode ? 'bg-slate-900/80 border-slate-700' : 'bg-slate-50 border-gray-200'
                }`}>
                    <div className="flex items-center gap-1.5 text-xs opacity-60">
                        <CheckCircle2 size={14} className="text-green-500" />
                        <span>Segurança: Nenhum token completo ou credencial sensível é gravado em arquivos estáticos.</span>
                    </div>
                    <Button variant="secondary" onClick={onClose} className="text-xs">
                        Fechar Detalhes
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default IntegrationDetailsModal;
